import { TemplatizedValue } from '../../components/jsonEditor/componentUI/templatizedFieldComponent';
import { validateEditorNodeSettings } from '../../components/jsonEditor/additionalNodes'
import jsonXMLConstants from '../../constants/jsonXMLConstants';
import constants from '../../constants/jsonXMLConstants';
import { getKeyedValue, getRealResolverInfo, isRealResolver, isSpecialResolver, parseToBoolean } from '../utils';
import {
  JE_JsonValidationErrorReport,
  JE_JsonValidationErrorReportStore,
  JE_JsonValidationErrorReportType,
  JE_FCValidationChart,
  JE_FCValidationNode,
  JE_JsonValidationContext
} from '../interfaces';
import { getAdditionalNodeOutputClaims } from '../../components/jsonEditor/additionalNodes/additionalNodes';
import { addClaimErrorReport, addNodeErrorReport, validateClaimExists, validateClaimIsSet, validateClaimPresence } from './utils';
import { validateTransformJson } from '../mappedClaimTransforms/mappedClaimTransforms';
const buildGraph = (flowcharts: { [id: string]: any }, nodeData: { [id: string]: any }) => {
  let flowchartIndices: { [flowId: string]: JE_FCValidationChart } = {};
  let nodes: { [nodeId: string]: JE_FCValidationNode } = {};
  Object.keys(flowcharts).forEach((flowchartId: string) => {
    let fNodes: Set<string> = new Set();
    if (flowcharts[flowchartId].nodes.length > 0) {
      flowcharts[flowchartId].nodes.forEach((fcNode: any) => {
        if (nodeData[fcNode.id]) {
          fNodes.add(fcNode.id);
          nodes[fcNode.id] = {
            children: [],
            allChildRefs: [],
            parents: [],
            displayLabel: fcNode.data.label,
            flowId: flowchartId,
            nodeData: nodeData[fcNode.id],
            nodeId: fcNode.id,
            parentEmittedClaims: new Set(),
            availableClaimsByParent: {},
            intersectionAvailableClaims: new Set(),
            inheritedSSOContexts: [],
            inheritedSSODescent: [],
            descendsFromSSOSession: false,
            ssoSessionExists: false,
            inSSOSessionScope: false
          };
        }
      });

      flowcharts[flowchartId].edges.forEach((edge: any) => {
        if (nodeData[edge.source] && nodeData[edge.target]) {
          if (
            !nodes[edge.source].children.some((chId) => {
              return chId === edge.target;
            })
          ) {
            if (edge.sourceHandle === 'down') {
              // Conditional node true branch; child order matters
              nodes[edge.source].children.unshift(edge.target);
            } else {
              nodes[edge.source].children.push(edge.target);
            }
            nodes[edge.target].parents.push(edge.source);
          }
          nodes[edge.source].allChildRefs.push({ nodeId: edge.target, edgeHandle: edge.sourceHandle });
        }
      });
      // Traverse and discard nodes not connected to the root.
      // TODO: Need the JSON from the editor to indicate the root. In the meantime, assume the first node found with no parents is the root.
      let potentialRootKeys: string[] = [];
      fNodes.forEach((key: string) => {
        if (nodes[key].parents.length == 0) {
          potentialRootKeys.push(key);
        }
      });
      if (potentialRootKeys.length < 1) {
        throw 'No root found for flowchart nodes - a cycle is likely';
      }
      flowchartIndices[flowchartId] = {
        flowId: flowchartId,
        flowName: flowcharts[flowchartId].flowchartName,
        nodes: fNodes,
        connectedNodes: new Set(),
        rootNodeId: '',
        topologicalOrder: [],
        flowType:
          flowcharts[flowchartId].flowchartType === 'formSubmitAction'
            ? 'submitAction'
            : flowcharts[flowchartId].flowchartType === 'userInfoEndpoint'
              ? 'userInfoEndpoint'
              : flowcharts[flowchartId].flowchartType === 'emailVerificationSendAction'
                ? 'emailVerificationSendAction'
                : 'userJourney',
        chartOutputClaims: new Set(),
      };
    }
  });
  return { flowchartIndices, nodes };
};

const validatePasswordComplexityConstraints = (
  vCtx: JE_JsonValidationContext,
  options: any[],
  lengthInfo?: [number, number],
  minConstraints?: number,
) => {
  let nCharacterSets = 0;
  let hasLength = false;
  options.forEach((complexityOpt) => {
    if (!constants.passwordComplexityOptions.includes(complexityOpt)) {
      addClaimErrorReport('password', vCtx, `Unknown password complexity option: ${complexityOpt}`);
    }
    if (constants.passwordComplexityCharacterSetOptions.includes(complexityOpt)) {
      nCharacterSets += 1;
    }
    if (complexityOpt === 'length') {
      hasLength = true;
    }
  });
  if (nCharacterSets > 0) {
    if (!minConstraints) {
      addClaimErrorReport(
        'password',
        vCtx,
        `Password complexity settings include character set requirements, but no minimum number to match is specified`,
      );
    } else {
      if (minConstraints < 1) {
        addClaimErrorReport(
          'password',
          vCtx,
          `Password complexity settings include character set requirements, but minimum constraint groups to match is not at least 1`,
        );
      }
      if (minConstraints > nCharacterSets) {
        addClaimErrorReport(
          'password',
          vCtx,
          `Minimum constraint groups to match in password complexity settings exceeds number of character set groups selected`,
        );
      }
    }
  }
  if (hasLength) {
    if (!lengthInfo) {
      addClaimErrorReport(
        'password',
        vCtx,
        'Password complexity enables length requirement but provides no length bounds',
      );
    } else {
      if (lengthInfo[0] < 1) {
        addClaimErrorReport(
          'password',
          vCtx,
          'Minimum length requirement for password including a length constraint must be at least 1',
        );
      }
      if (lengthInfo[1] < lengthInfo[0]) {
        addClaimErrorReport(
          'password',
          vCtx,
          'Maximum length requirement for password complexity cannot be less than the minimum length requirement',
        );
      }
      if (lengthInfo[1] > 250) {
        addClaimErrorReport(
          'password',
          vCtx,
          'Maximum length requirement for password complexity exceeds ADB2C string attribute character limit of 250',
        );
      }
    }
  }
};

const parseClaims = (vCtx: JE_JsonValidationContext, claimData: { [index: string]: any }) => {
  Object.keys(claimData).forEach((claimId) => {
    vCtx.claims[claimId] = {
      claimId: claimData[claimId].id,
      dataType: claimData[claimId].dataType,
      userInputType: claimData[claimId].userInputType,
    };
    if (claimId === 'password') {
      validatePasswordComplexityConstraints(
        vCtx,
        claimData[claimId].options,
        claimData[claimId].length
          ? [parseInt(claimData[claimId].length[0]), parseInt(claimData[claimId].length[1])]
          : undefined,
        claimData[claimId].minConstraints
          ? parseInt(claimData[claimId].minConstraints)
          : claimData[claimId].minConstraints,
      );
    }
  });
};

const parseAttributes = (vCtx: JE_JsonValidationContext, attrData: { [index: string]: any }) => {
  Object.keys(attrData).forEach((attrId) => {
    vCtx.attributes[attrData[attrId].id] = {
      id: attrData[attrId].id,
      dataType: attrData[attrId].dataType,
    };
  });
};

const addOutputClaimsIfPresent = (claimCollection: Set<string>, claimId?: string) => {
  if (claimId) {
    claimCollection.add(claimId);
  }
};

// Collect the sets of output claims a profile outputs (or makes available to validation flows).
const getNodeOutputClaims = (vCtx: JE_JsonValidationContext, nodeId: string) => {
  let node = vCtx.nodes[nodeId];
  let outputClaims: Set<string> = new Set();
  let pwOutputClaims: Set<string> = new Set();
  switch (node.nodeData.componentType) {
    case constants.editorComponentTypes.signIn:
      if (node.nodeData.links.localAccountSignIn) {
        addOutputClaimsIfPresent(outputClaims, node.nodeData.username);
        if (node.nodeData.showPasswordTextbox) {
          addOutputClaimsIfPresent(pwOutputClaims, 'password');
        }
      }
      break;
    case constants.editorComponentTypes.userInput:
      node.nodeData.attributes.forEach((attrData: any) => {
        if (vCtx.claims[attrData.attributeName].userInputType === 'Password') {
          addOutputClaimsIfPresent(pwOutputClaims, attrData.attributeName);
        } else {
          addOutputClaimsIfPresent(outputClaims, attrData.attributeName);
        }
      });

      if (node.nodeData.validateEmail) {
        if (node.nodeData.email.valEmailType === 'newEmail') {
          addOutputClaimsIfPresent(outputClaims, node.nodeData.email.newEmail.attributeSource);
        } else {
          addOutputClaimsIfPresent(outputClaims, node.nodeData.email.valPrevEmail.emailAttribute);
        }
      }
      break;
    case constants.editorComponentTypes.google:
      addOutputClaimsIfPresent(outputClaims, 'foreignId');
      node.nodeData.attributes.forEach((attrData: any) => {
        addOutputClaimsIfPresent(outputClaims, attrData.claim);
      });
      node.nodeData.extraOutputClaims.forEach((claimData: any) => {
        addOutputClaimsIfPresent(outputClaims, claimData.claimId);
      })
      break;
    case constants.editorComponentTypes.aadRead:
      node.nodeData.attributes.forEach((attrData: any) => {
        addOutputClaimsIfPresent(outputClaims, attrData.claim);
      });
      break;
    case constants.editorComponentTypes.aadUpdate:
      if (node.nodeData.mapObjectId && node.nodeData.mappedClaim) {
        addOutputClaimsIfPresent(outputClaims, node.nodeData.mappedClaim);
      }
      break;
    case constants.editorComponentTypes.aadCreate:
      if (node.nodeData.mapObjectId && node.nodeData.mappedClaim) {
        addOutputClaimsIfPresent(outputClaims, node.nodeData.mappedClaim);
      }
      if (node.nodeData.mapUserPrincipalName && node.nodeData.upnClaim) {
        addOutputClaimsIfPresent(outputClaims, node.nodeData.upnClaim);
      }
      break;
    case constants.editorComponentTypes.usernamePasswordValidation:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.claim);
      break;
    case constants.editorComponentTypes.rest:
      node.nodeData.parameters.forEach((paramData: any) => {
        if (paramData.type === 'output') {
          addOutputClaimsIfPresent(outputClaims, paramData.claimName);
        }
      });
      break;
    case constants.editorComponentTypes.mfa:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.enteredPhoneNumberClaim);
      addOutputClaimsIfPresent(outputClaims, node.nodeData.newNumberFlagClaim);
      break;
    case constants.editorComponentTypes.webAuthnRegistration:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.successClaim);
      break;
    case constants.editorComponentTypes.webAuthnAuthentication:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.successClaim);
      addOutputClaimsIfPresent(outputClaims, node.nodeData.idClaim);
      break;
    case constants.editorComponentTypes.webAuthnAvailability:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.availabilityClaim);
      break;
    case constants.editorComponentTypes.webAuthnCredentialsExist:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.existsClaim);
      break;
    case constants.editorComponentTypes.conditionalAccessEvaluation:
      addOutputClaimsIfPresent(outputClaims, node.nodeData.changePasswordChallenge);
      addOutputClaimsIfPresent(outputClaims, node.nodeData.mfaChallenge);
      addOutputClaimsIfPresent(outputClaims, node.nodeData.blockChallenge);
      break;
    case constants.editorComponentTypes.authorizeToken:
      node.nodeData.claims.forEach((clm: any) => {
        addOutputClaimsIfPresent(outputClaims, clm.id);
      });
      break;
    case constants.editorComponentTypes.userInfoIssuer:
      break;
    case constants.editorComponentTypes.conditional:
      break;
    case constants.editorComponentTypes.tokenIssuer:
      break;
    case constants.editorComponentTypes.sendEmailOTP:
      break;
    case constants.editorComponentTypes.sessionExists:
      vCtx.ssoSession.persistedClaims.forEach((pClm) => {
        addOutputClaimsIfPresent(outputClaims, pClm);
      });
      vCtx.ssoSession.outputClaims.forEach((oClm) => {
        addOutputClaimsIfPresent(outputClaims, oClm.claimId);
      });
      break;
    case constants.editorComponentTypes.oidcIdP:
      addOutputClaimsIfPresent(outputClaims, 'foreignId');
      node.nodeData.outputClaims.forEach((outClaim: any) => {
        addOutputClaimsIfPresent(outputClaims, outClaim.claimId);
      });
      break;
    case constants.editorComponentTypes.samlIdP:
      addOutputClaimsIfPresent(outputClaims, 'foreignId');
      node.nodeData.outputClaims.forEach((outClaim: any) => {
        addOutputClaimsIfPresent(outputClaims, outClaim.claimId);
      });
      break;
    case constants.editorComponentTypes.setClaims:
      Object.keys(node.nodeData.setClaims).forEach((scId: any) => {
        addOutputClaimsIfPresent(outputClaims, node.nodeData.setClaims[scId].destClaim)
      });
      break;
    default:
      getAdditionalNodeOutputClaims(
        node.nodeData.componentType,
        node.nodeData,
        vCtx
      ).forEach(
        (outClaimId: string) => addOutputClaimsIfPresent(
          outputClaims, outClaimId
        )
      );
      break;
  }
  return { outputClaims, pwOutputClaims };
};

const reduceOr = (booleans: boolean[]) => {
  return booleans.reduce((res: boolean, nxt: boolean) => { return res || nxt; }, false);
}

const reduceAnd = (booleans: boolean[]) => {
  return booleans.reduce((res: boolean, nxt: boolean) => { return res && nxt; }, true);
}

const processFlowchartClaims = (
  vCtx: JE_JsonValidationContext,
  flowId: string,
  availableInputs: Set<string>,
  ssoSessionDescent: boolean,
  inSSOSessionScope: boolean,
  ssoSessionExists: boolean
) => {
  // Do the submit action flows inline so we know what input claims are available to them.
  let flowchart = vCtx.flowcharts[flowId];
  availableInputs.forEach((avId) => {
    vCtx.nodes[flowchart.topologicalOrder[0]].parentEmittedClaims.add(avId);
  });
  vCtx.nodes[flowchart.topologicalOrder[0]].ssoSessionExists = ssoSessionExists;
  vCtx.nodes[flowchart.topologicalOrder[0]].descendsFromSSOSession = ssoSessionDescent;
  vCtx.nodes[flowchart.topologicalOrder[0]].inSSOSessionScope = inSSOSessionScope;
  vCtx.nodes[flowchart.topologicalOrder[0]].inheritedSSODescent = [ssoSessionDescent];
  flowchart.topologicalOrder.forEach((nodeId) => {
    let curNode = vCtx.nodes[nodeId];
    // Compute the intersection of available claims which have no paths where they are guaranteed unset.
    curNode.parentEmittedClaims.forEach((claimId) => {
      if (
        Object.keys(curNode.availableClaimsByParent).reduce((prev, curKey) => {
          return prev && curNode.availableClaimsByParent[curKey].has(claimId);
        }, true)
      ) {
        curNode.intersectionAvailableClaims.add(claimId);
      }
    });
    curNode.descendsFromSSOSession = (curNode.inheritedSSODescent.length > 0) && reduceAnd(curNode.inheritedSSODescent);
    curNode.inSSOSessionScope = (curNode.inheritedSSOContexts.length > 0) && (reduceAnd(curNode.inheritedSSOContexts) || (!reduceOr(curNode.inheritedSSOContexts)));
    curNode.ssoSessionExists = curNode.inSSOSessionScope && reduceAnd(curNode.inheritedSSOContexts);

    let { outputClaims, pwOutputClaims } = getNodeOutputClaims(vCtx, nodeId);
    if (curNode.nodeData.componentType === constants.editorComponentTypes.sessionExists) {
      curNode.children.forEach((chId) => {
        vCtx.nodes[chId].inheritedSSODescent.push(true);
        if (!vCtx.nodes[chId].availableClaimsByParent.hasOwnProperty(nodeId)) {
          vCtx.nodes[chId].availableClaimsByParent[nodeId] = new Set();
        }
        curNode.parentEmittedClaims.forEach((pcId) => {
          vCtx.nodes[chId].parentEmittedClaims.add(pcId);
        });
        curNode.intersectionAvailableClaims.forEach((iId) => {
          vCtx.nodes[chId].availableClaimsByParent[nodeId].add(iId);
        });
        if (
          curNode.allChildRefs
            .filter((chRef) => {
              return chRef.nodeId === chId;
            })
            .every((chRef) => {
              return chRef.edgeHandle === 'down';
            })
        ) {
          outputClaims.forEach((ocId) => {
            if (vCtx.claims[ocId].userInputType !== 'Password') {
              vCtx.nodes[chId].parentEmittedClaims.add(ocId);
              vCtx.nodes[chId].availableClaimsByParent[nodeId].add(ocId);
            }
          });
          vCtx.nodes[chId].inheritedSSOContexts.push(false);
        } else if (
          curNode.allChildRefs
            .filter((chRef) => {
              return chRef.nodeId == chId;
            })
            .every((chRef) => {
              return chRef.edgeHandle !== 'down';
            })
        ) {
          vCtx.nodes[chId].inheritedSSOContexts.push(true);
        }
      });
    } else {
      outputClaims.forEach((ocId) => {
        if (vCtx.claims[ocId].userInputType !== 'Password') {
          flowchart.chartOutputClaims.add(ocId);
        }
      });
      let submitFlowBubbledOutputClaims: Set<string> = new Set();
      if (
        curNode.nodeData.formSubmitAction &&
        curNode.nodeData.formSubmitAction.id &&
        vCtx.flowcharts.hasOwnProperty(curNode.nodeData.formSubmitAction.id)
      ) {
        let availableInputs: Set<string> = new Set();
        outputClaims.forEach((cId) => availableInputs.add(cId));
        pwOutputClaims.forEach((cId) => availableInputs.add(cId));
        curNode.intersectionAvailableClaims.forEach((cId) => availableInputs.add(cId));
        processFlowchartClaims(vCtx, curNode.nodeData.formSubmitAction.id, availableInputs, curNode.descendsFromSSOSession, curNode.inSSOSessionScope, curNode.ssoSessionExists);
        submitFlowBubbledOutputClaims = vCtx.flowcharts[curNode.nodeData.formSubmitAction.id].chartOutputClaims!;
      }
      if (
        curNode.nodeData.validateEmail &&
        curNode.nodeData.emailVerificationSendAction &&
        curNode.nodeData.emailVerificationSendAction.id &&
        vCtx.flowcharts.hasOwnProperty(curNode.nodeData.emailVerificationSendAction.id)
      ) {
        let availableInputs: Set<string> = new Set();
        if (curNode.nodeData.email) {
          if (curNode.nodeData.email.valEmailType === 'newEmail') {
            availableInputs.add(curNode.nodeData.email.newEmail.attributeSource);
          } else {
            availableInputs.add(curNode.nodeData.email.valPrevEmail.emailAttribute);
          }
        }
        curNode.intersectionAvailableClaims.forEach((cId) => availableInputs.add(cId));
        processFlowchartClaims(
          vCtx,
          curNode.nodeData.emailVerificationSendAction.id,
          availableInputs,
          curNode.descendsFromSSOSession,
          curNode.inSSOSessionScope,
          curNode.ssoSessionExists
        );
      }
      curNode.children.forEach((chId) => {
        if (curNode.inSSOSessionScope) {
          vCtx.nodes[chId].inheritedSSOContexts.push(curNode.ssoSessionExists);
        }
        vCtx.nodes[chId].inheritedSSODescent.push(curNode.descendsFromSSOSession);

        if (!vCtx.nodes[chId].availableClaimsByParent.hasOwnProperty(nodeId)) {
          vCtx.nodes[chId].availableClaimsByParent[nodeId] = new Set();
        }
        curNode.parentEmittedClaims.forEach((pcId) => {
          vCtx.nodes[chId].parentEmittedClaims.add(pcId);
        });
        if (
          curNode.nodeData.componentType !== 'signIn' ||
          vCtx.nodes[chId].nodeData.linkType === 'localAccountSignIn'
        ) {
          outputClaims.forEach((ocId) => {
            vCtx.nodes[chId].parentEmittedClaims.add(ocId);
            vCtx.nodes[chId].availableClaimsByParent[nodeId].add(ocId);
          });
          submitFlowBubbledOutputClaims.forEach((sfcId) => {
            vCtx.nodes[chId].parentEmittedClaims.add(sfcId);
            vCtx.nodes[chId].availableClaimsByParent[nodeId].add(sfcId);
          });
        }
        curNode.intersectionAvailableClaims.forEach((iId) => {
          vCtx.nodes[chId].availableClaimsByParent[nodeId].add(iId);
        });
      });
    }
  });
};

const nodeHasChildWithNamePrefix = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode, prefix: string) => {
  return node.children.some((chId) => vCtx.nodes[chId].nodeId.startsWith(prefix));
};

const getSignInNodeChildErrorMessage = (linkOption: string, childType: string, enabled: boolean) => {
  return `${linkOption} link ${enabled ? 'enabled' : 'disabled'} for sign-in node but ${enabled ? 'no' : 'a'
    } ${childType} child node is present`;
};

const nodeHasChildOfType = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode, compType: string) => {
  return node.children.some((chId) => vCtx.nodes[chId].nodeData.componentType === compType);
};

const validateSignInLinkChildNamePrefix = (
  vCtx: JE_JsonValidationContext,
  node: JE_FCValidationNode,
  link: boolean,
  childType: string,
  linkDesc: string,
) => {
  if (link) {
    if (!nodeHasChildWithNamePrefix(vCtx, node, childType)) {
      addNodeErrorReport(node.nodeId, vCtx, getSignInNodeChildErrorMessage(linkDesc, childType, true));
    }
  } else {
    if (nodeHasChildWithNamePrefix(vCtx, node, childType)) {
      addNodeErrorReport(node.nodeId, vCtx, getSignInNodeChildErrorMessage(linkDesc, childType, false));
    }
  }
};

const validateSignInLinkChildComponentType = (
  vCtx: JE_JsonValidationContext,
  node: JE_FCValidationNode,
  link: boolean,
  childType: string,
  linkDescA: string,
  linkDescB: string,
) => {
  if (link) {
    if (!nodeHasChildOfType(vCtx, node, childType)) {
      addNodeErrorReport(node.nodeId, vCtx, getSignInNodeChildErrorMessage(linkDescA, linkDescB, true));
    }
  } else {
    if (nodeHasChildOfType(vCtx, node, childType)) {
      addNodeErrorReport(node.nodeId, vCtx, getSignInNodeChildErrorMessage(linkDescA, linkDescB, false));
    }
  }
};

const getTemplatizableFieldValue = (vCtx: JE_JsonValidationContext, fld: TemplatizedValue) => {
  return (fld.value === undefined ? fld as unknown as string : (fld.templatized ? (vCtx.settings ? vCtx.settings[fld.value].name : fld.value) : fld.value));
}

const templatizableFieldIsEmpty = (fld: TemplatizedValue) => {
  return (!fld) || (!fld.value)
}

const idpMessageNames: { [index: string]: [string, string] } = {
  employeeLogin: ['Employee login', 'employee login'],
  facebook: ['Facebook', 'Facebook'],
  google: ['Google', 'Google'],
  discord: ['Discord', 'Discord'],
  linkedIn: ['LinkedIn', 'LinkedIn'],
  weChat: ['WeChat', 'WeChat'],
};

const validateSessionManagementSettings = (
  vCtx: JE_JsonValidationContext,
  node: JE_FCValidationNode,
  smSettings: any,
) => {
  if (smSettings) {
    if (smSettings.option === 'skipForSpecificSession') {
      if (['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[node.flowId].flowType)) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          'Skipping profiles on session flags is not available within subflows. Consider configuring the parent profile to skip on the flag.',
        );
      } else {
        if (!smSettings.sessionId) {
          addNodeErrorReport(node.nodeId, vCtx, 'Component is configured to skip if session flag exists, but no session flag is defined');
        }
        if (!node.descendsFromSSOSession) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `${(smSettings.sessionId === '_stepUpMfa') ? 'Step-up MFA' : 'Session flags'} can only be used on components that descend from an SSO Session Exists component`,
          );
        }
      }
    } else if (smSettings.option === 'useGlobalSession') {
      if (['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[node.flowId].flowType)) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          'Storing claims into the SSO session is not available within subflows. Consider configuring the parent profile to atore the relevant claims.',
        );
      } else if (!(node.inSSOSessionScope && node.ssoSessionExists)) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          'Storing claims in the SSO sesson can only be done on the not-exists branch of an SSO Session Exists component'
        );
      } else {
        let { outputClaims, pwOutputClaims } = getNodeOutputClaims(vCtx, node.nodeId);
        let bubbledSubmitActionClaims: Set<string> = node.nodeData.formSubmitAction
          ? vCtx.flowcharts.hasOwnProperty(node.nodeData.formSubmitAction.id)
            ? vCtx.flowcharts[node.nodeData.formSubmitAction.id].chartOutputClaims
            : new Set()
          : new Set();
        if (
          !vCtx.ssoSession.persistedClaims.some((pClm) => {
            return outputClaims.has(pClm) || bubbledSubmitActionClaims.has(pClm);
          })
        ) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Component is configured to store claims in SSO sesison, but the component does not output any claims stored by this session`,
          );
        }
      }
    }
  }
};

const validateNodeChildCount = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode, expNumber: number) => {
  if (node.children.length < expNumber) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      `Node expecting ${expNumber} child nodes only has ${node.children.length} child connections`,
    );
  }
  if (node.children.length > expNumber) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      `Node expecting ${expNumber} child nodes has too many child connections (${node.children.length})`,
    );
  }
};

const validateSignInNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'Sign-in node not a valid element inside a submit action flow');
  }
  if (node.children.length === 0) {
    addNodeErrorReport(node.nodeId, vCtx, 'Sign-in node has no children. At least one link must be enabled.');
  }
  // Validate local account signin settings
  if (node.nodeData.links.localAccountSignIn) {
    if (!node.nodeData.username) {
      addNodeErrorReport(node.nodeId, vCtx, 'No user name claim selected in sign-in node');
    } else if (!vCtx.claims.hasOwnProperty(node.nodeData.username)) {
      addNodeErrorReport(node.nodeId, vCtx, 'Unknown claim ID in user name claim of sign-in node');
    } else if (vCtx.claims[node.nodeData.username].dataType !== 'string') {
      addNodeErrorReport(node.nodeId, vCtx, 'Illegal claim type for user name claim; must be string');
    }
    if (!nodeHasChildWithNamePrefix(vCtx, node, 'localAccountSignIn')) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        getSignInNodeChildErrorMessage('Local account signin', 'localAccountSignIn', true),
      );
    }
  } else {
    if (parseToBoolean(node.nodeData.showPasswordTextbox)) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        'Local account signin disabled for sign-in node but show password textbox set to true',
      );
    }
    if (node.children.some((chId) => vCtx.nodes[chId].nodeId.startsWith('localAccountSignIn'))) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        getSignInNodeChildErrorMessage('Local account signin', 'localAccountSignIn', false),
      );
    }
    if (node.nodeData.formSubmitAction.id) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        'Local account signin disabled for sign-in node, but node has submit actions defined',
      );
    }
  }
  // Validate other non-federated IdP links
  validateSignInLinkChildNamePrefix(
    vCtx,
    node,
    node.nodeData.links.forgotPassword,
    'forgotPassword',
    'Forgot password',
  );
  validateSignInLinkChildNamePrefix(vCtx, node, node.nodeData.links.otp, 'otp', 'OTP');
  validateSignInLinkChildNamePrefix(
    vCtx,
    node,
    node.nodeData.links.createNewAccount,
    'createNewAccount',
    'Create new account',
  );
  // Validate federated IdP links
  Object.keys(node.nodeData.links.socialProviders).forEach((idpType) => {
    validateSignInLinkChildComponentType(
      vCtx,
      node,
      node.nodeData.links.socialProviders[idpType],
      idpType,
      idpMessageNames[idpType][0],
      idpMessageNames[idpType][1],
    );
  });
  // Validate custom links
  if (node.nodeData.nodeLinks) {
    node.nodeData.nodeLinks.forEach((linkNodeId: string) => {
      if (!vCtx.nodes.hasOwnProperty(linkNodeId) || vCtx.nodes[linkNodeId].flowId !== node.flowId) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Sign-in node references custom link node '${vCtx.nodes[linkNodeId]?.displayLabel ?? linkNodeId}' that does not exist in flowchart`,
        );
      } else if (vCtx.nodes[linkNodeId].nodeData.componentType === constants.editorComponentTypes.signIn) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Custom link component '${vCtx.nodes[linkNodeId].displayLabel}' is a sign-in node and cannot be a link node for another sign-in component`,
        );
      }
    });
  }
  // Validate session management
  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
  if ((node.nodeData.sessionManagement.option === 'skipForSpecificSession') && (node.children.length > 1)) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      `Sign-in node has more than one child link and cannot be skipped if a specific session flag exists`
    );
  }
};

const validateUserInputPageNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'User input node not a valid element inside a submit action flow');
  }
  validateNodeChildCount(vCtx, node, 1);
  if (
    !node.nodeData.validateEmail &&
    node.nodeData.attributes.length === 0 &&
    node.nodeData.linkType !== 'localAccountSignIn'
  ) {
    addNodeErrorReport(node.nodeId, vCtx, 'User input node has no displayed fields or email validation enabled');
  }
  let seenClaims: Set<string> = new Set();
  node.nodeData.attributes.forEach((attrData: any) => {
    if (seenClaims.has(attrData.attributeName)) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Claim '${attrData.attributeName}' marked for display more than once in same user input node`,
      );
    }
    seenClaims.add(attrData.attributeName);
    if (validateClaimExists(vCtx, node, attrData.attributeName)) {
      let userInputType = vCtx.claims[attrData.attributeName].userInputType;
      if (!userInputType) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim '${attrData.attributeName}' with no userInputType defined is selected as a displayed field in user input node`,
        );
      } else if (!attrData.readOnly && (userInputType === 'Readonly' || userInputType === 'Paragraph')) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim '${attrData.attributeName}' with a readonly userInputType is selected as an editable field in user input node`,
        );
      } else if (
        vCtx.claims[attrData.attributeName].dataType === 'dateTime' &&
        vCtx.claims[attrData.attributeName].userInputType !== 'Readonly' &&
        vCtx.claims[attrData.attributeName].userInputType !== 'Paragraph' &&
        attrData.readOnly
      ) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim '${attrData.attributeName}' is an uncopyable data type and cannot be displayed readonly in user input pages if it has a non-readonly user input type`,
        );
      }
      let claimType = vCtx.claims[attrData.attributeName].dataType;
      if (attrData.prefillValue === 'fromAnotherAttribute') {
        if (!(claimType === vCtx.claims[attrData.attributeSource].dataType)) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Claim '${attrData.attributeName}' prefilled from claim of differing data type in user input node`,
          );
        } else if (claimType === 'dateTime' && attrData.attributeName !== attrData.attributeSource) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Claim '${attrData.attributeName}' is an uncopyable data type and cannot be prefilled from a different claim in user input pages`,
          );
        }
        validateClaimIsSet(
          vCtx,
          node,
          attrData.attributeName,
          `used for prefilling claim '${attrData.attributeSource}'`,
        );
      } else if (attrData.prefillValue === 'sessionContext') {
        if (isRealResolver(attrData.sessionContext)) {
          let resolverInfo = getRealResolverInfo(attrData.sessionContext);
          if (!resolverInfo) {
            addNodeErrorReport(node.nodeId, vCtx, `Unknown session context item '${attrData.sessionContext}'`);
          } else if (!resolverInfo.validFor.includes(vCtx.flowcharts[node.flowId].issuedTokenType!)) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Session context item '${attrData.sessionContext}' not available for the token type issued by its containing parent flowchart`,
            );
          } else if (claimType !== 'string') {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${attrData.attributeName}' prefilled from session context is of non-string type '${vCtx.claims[attrData.attributeName].dataType
              }'`,
            );
          }
        } else if (isSpecialResolver(attrData.sessionContext)) {
          let specialResolverInfo =
            constants.sessionContexts.specials[
            attrData.sessionContext as keyof typeof constants.sessionContexts.specials
            ];
          if (claimType !== specialResolverInfo.dataType) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${attrData.attributeName}' of type '${claimType}' prefilled from session context of non-matching data type '${specialResolverInfo.dataType}'`,
            );
          }
        }
      }
    }
  });
  if (node.nodeData.validateEmail) {
    if (node.nodeData.email.valEmailType === 'newEmail' && node.nodeData.email.newEmail.prefillValue) {
      validateClaimIsSet(
        vCtx,
        node,
        node.nodeData.email.newEmail.attributeSource,
        'used for email validation and set as prefilled',
      );
    } else if (node.nodeData.email.valEmailType === 'valPrevEmail') {
      validateClaimExists(vCtx, node, node.nodeData.email.valPrevEmail.emailAttribute);
      if (!node.nodeData.email.valPrevEmail.sourceType || node.nodeData.email.valPrevEmail.sourceType === 'claim') {
        validateClaimIsSet(
          vCtx,
          node,
          node.nodeData.email.valPrevEmail.emailSource
            ? node.nodeData.email.valPrevEmail.emailSource
            : node.nodeData.email.valPrevEmail.emailAttribute,
          'used as existing input for email validation',
        );
      } else if (node.nodeData.email.valPrevEmail.sourceType === 'resolver') {
        if (isRealResolver(node.nodeData.email.valPrevEmail.emailSource)) {
          let resolverInfo = getRealResolverInfo(node.nodeData.email.valPrevEmail.emailSource);
          if (!resolverInfo) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Unknown session context item '${node.nodeData.email.valPrevEmail.emailSource}'`,
            );
          } else if (!resolverInfo.validFor.includes(vCtx.flowcharts[node.flowId].issuedTokenType!)) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Session context item '${node.nodeData.email.valPrevEmail.emailSource}' not available for the token type issued by its containing parent flowchart`,
            );
          } else if (vCtx.claims[node.nodeData.email.valPrevEmail.emailAttribute].dataType !== 'string') {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${node.nodeData.email.valPrevEmail.emailAttribute
              }' prefilled from session context is of non-string type '${vCtx.claims[node.nodeData.email.valPrevEmail.emailAttribute].dataType
              }'`,
            );
          }
        }
      } else {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          'Unknown email prefill source type in email validation of user input component',
        );
      }
    }
  }
  if (node.nodeData.linkType !== 'localAccountSignIn') {
    validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
  }
};

const validateGoogleNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  let seenClaims: Set<string> = new Set();
  node.nodeData.attributes.forEach((attrData: any) => {
    if (validateClaimExists(vCtx, node, attrData.claim)) {
      if (seenClaims.has(attrData.claim)) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim '${attrData.claim}' mapped to more than one partner attribute in Google node`,
        );
      }
      seenClaims.add(attrData.claim);
      if (vCtx.claims[attrData.claim].dataType !== 'string') {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim '${attrData.claim}' is of incorrect type '${vCtx.claims[attrData.claim].dataType
          } to map to partner attribute from Google node`,
        );
      }
    }
  });
  node.nodeData.extraOutputClaims.forEach((clData: any) => {
    if (validateClaimExists(vCtx, node, clData.claimId)) {
      if (seenClaims.has(clData.claimId)) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Output claim '${clData.claimId}' mapped to more than one value in Google node`,
        );
      }
      seenClaims.add(clData.claimId);
      if (!clData.defaultValue) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Extra output claim '${clData.claimId}' in Google node has no default value defined`
        );
      }
    }
  });
  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateUsernamePasswordValidationNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'UsernamePasswordValidation is not a valid component outside of a submit action',
    );
  }
  let flowParentNodeId = vCtx.flowcharts[node.flowId].subflowParentNode!;
  let flowParentNode = vCtx.nodes[flowParentNodeId];
  if (flowParentNode.nodeData.componentType === 'signIn') {
    if (flowParentNode.nodeData.usernameType !== node.nodeData.usernameType) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `UsernamePasswordValidation node username type '${node.nodeData.usernameType}' does not match its submit action parent signIn node's '${flowParentNode.nodeData.usernameType}'`,
      );
    }
    if (flowParentNode.nodeData.username !== node.nodeData.username) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `UsernamePasswordValidation node username claim '${node.nodeData.username}' does not match its submit action parent signIn node's '${flowParentNode.nodeData.username}'`,
      );
    }
  }
  if (validateClaimExists(vCtx, node, node.nodeData.username)) {
    if (vCtx.claims[node.nodeData.username].dataType !== 'string') {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Invalid data type for UsernamePasswordValidation username claim: '${node.nodeData.username}'`,
      );
    }
  }
  validateClaimIsSet(vCtx, node, node.nodeData.username, 'used for username field');
  validateClaimIsSet(vCtx, node, 'password', 'used in validation');
  if (validateClaimExists(vCtx, node, node.nodeData.claim)) {
    if (!(vCtx.claims[node.nodeData.claim].dataType === 'string')) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Claim mapping of '${node.nodeData.claim}' is of invalid type '${vCtx.claims[node.nodeData.claim].dataType
        }' to map to objectId output`,
      );
    }
  }
  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateAADIndexProperty = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  let keyedOnForeignID = node.nodeData.indexProperty.id === 'foreign objectid';
  let indexClaimId = node.nodeData.indexProperty.claim;

  if (!indexClaimId) {
    addNodeErrorReport(node.nodeId, vCtx, 'No claim selected for index attribute of AAD operation');
  } else if (validateClaimExists(vCtx, node, indexClaimId)) {
    if (keyedOnForeignID && vCtx.claims[indexClaimId].dataType !== 'userIdentity') {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Claim '${indexClaimId}' of invalid type '${vCtx.claims[indexClaimId].dataType}' to index against 'userIdentity' type of foreign objectid`,
      );
    } else if (!keyedOnForeignID && vCtx.claims[indexClaimId].dataType !== 'string') {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Claim '${indexClaimId}' of invalid type '${vCtx.claims[indexClaimId].dataType}' to index against 'string' type of ${node.nodeData.indexProperty.id}`,
      );
    }
    validateClaimIsSet(vCtx, node, indexClaimId, 'used for index attribute in AAD operation');
  }
};

const validateAADReadNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    validateNodeChildCount(vCtx, node, 1);
  }
  // Validate the index property
  validateAADIndexProperty(vCtx, node);

  // Validate attributes.
  let seenClaims: Set<string> = new Set();
  if (node.nodeData.attributes.length == 0) {
    addNodeErrorReport(node.nodeId, vCtx, 'AAD read node contains no attribute mappings');
  }
  node.nodeData.attributes.forEach((attrData: any) => {
    if (seenClaims.has(attrData.claim)) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Claim '${attrData.claim}' mapped to multiple AAD attributes in read operation`,
      );
    }
    seenClaims.add(attrData.claim);
    if (validateClaimExists(vCtx, node, attrData.claim)) {
      if (vCtx.claims[attrData.claim].dataType !== vCtx.attributes[attrData.id].dataType) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim '${attrData.claim}' of type '${vCtx.claims[attrData.claim].dataType}' mapped to AAD attribute '${attrData.id
          }' of non-matching type '${vCtx.attributes[attrData.id].dataType}'`,
        );
      }
    }
  });

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateAADWriteMappings = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode, nodeType: string) => {
  // Validate the index property
  validateAADIndexProperty(vCtx, node);
  let keyedOnForeignID = node.nodeData.indexProperty.id === 'foreign objectid';
  let seenAttributes: Set<string> = new Set();
  let uiCollectionOps: Set<string> = new Set();
  let uiSeenClaims: Set<string> = new Set();
  if (node.nodeData.attributes.length == 0) {
    addNodeErrorReport(node.nodeId, vCtx, `AAD ${nodeType} node contains no attribute mappings`);
  }
  node.nodeData.attributes.forEach((attrData: any) => {
    if (seenAttributes.has(attrData.id) && attrData.id !== 'userIdentities') {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `AAD attribute '${attrData.id}' mapped to multiple claims in ${nodeType} operation`,
      );
    }
    seenAttributes.add(attrData.id);
    if (
      !(
        (attrData.id === 'password' &&
          attrData.claimOrFunc === 'Random' &&
          node.nodeData.componentType === 'aadCreate') ||
        attrData.claimOrFunc === 'Session Context' ||
        (attrData.defaultValue && attrData.alwaysUseDefaultValue)
      )
    ) {
      if (validateClaimExists(vCtx, node, attrData.claim)) {
        if (vCtx.attributes[attrData.id].dataType === 'userIdentityCollection') {
          if (keyedOnForeignID) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `UserIdentity collection cannot be simultaneously be key property and modified in mappings of AAD ${nodeType} operation`,
            );
          }
          if (uiSeenClaims.has(attrData.claim)) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${attrData.claim}' mapped to user identity collection multiple times in AAD ${nodeType} operation`,
            );
          }
          uiCollectionOps.add(attrData.operation);
          if (vCtx.claims[attrData.claim].dataType !== 'userIdentity') {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${attrData.claim}' of type '${vCtx.claims[attrData.claim].dataType}' mapped to AAD attribute '${attrData.id
              }' requiring a claim of type 'userIdentity'`,
            );
          }
        } else {
          if (vCtx.claims[attrData.claim].dataType !== vCtx.attributes[attrData.id].dataType) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${attrData.claim}' of type '${vCtx.claims[attrData.claim].dataType}' mapped to AAD attribute '${attrData.id
              }' of non-matching type '${vCtx.attributes[attrData.id].dataType}'`,
            );
          }
        }
        validateClaimIsSet(vCtx, node, attrData.claim, `used in AAD ${nodeType} attribute mapping`);
      }
    }
    if (attrData.claimOrFunc === 'Session Context') {
      if (isRealResolver(attrData.resolver)) {
        let resolverInfo = getRealResolverInfo(attrData.resolver);
        if (!resolverInfo) {
          addNodeErrorReport(node.nodeId, vCtx, `Unknown session context item '${attrData.resolver}'`);
        } else if (!resolverInfo.validFor.includes(vCtx.flowcharts[node.flowId].issuedTokenType!)) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Session context item '${attrData.resolver}' not available for the token type issued by its containing parent flowchart`,
          );
        } else if (vCtx.attributes[attrData.id].dataType !== 'string') {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `AAD attribute '${attrData.id}' being written from session context is of non-string type '${vCtx.attributes[attrData.id].dataType
            }'`,
          );
        }
        if (
          Object.keys(constants.sessionContexts.customKeyResolvers).some((k) => {
            return attrData.resolver.startsWith(
              constants.sessionContexts.customKeyResolvers[
                k as keyof typeof constants.sessionContexts.customKeyResolvers
              ].prefix,
            );
          })
        ) {
          if (attrData.resolver.split(':')[1]?.length <= 1) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `No key value provided for custom-key claim resolver mapped to AAD attribute '${attrData.id}'`,
            );
          }
        }
      } else if (isSpecialResolver(attrData.resolver)) {
        let specialType =
          constants.specialClaims[
            constants.sessionContexts.specials[attrData.resolver as keyof typeof constants.sessionContexts.specials]
              .claimId as keyof typeof constants.specialClaims
          ].dataType;
        if (vCtx.attributes[attrData.id].dataType !== specialType) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `AAD Attribute '${attrData.id}' of data type '${vCtx.attributes[attrData.id].dataType
            }' is written to from session context item of non-matching type ${specialType} in AAD write operation`,
          );
        }
      } else {
        addNodeErrorReport(node.nodeId, vCtx, `Reference to unknown session context item '${attrData.resolver}'`);
      }
    }
    if (attrData.claimOrFunc === 'Function') {
      if (attrData.transformation === 'string concatenation') {
        if (vCtx.attributes[attrData.id].dataType !== 'string') {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `String concatenation result mapped to AAD attribute '${attrData.id}' of non-string type '${vCtx.attributes[attrData.id].dataType
            }'`,
          );
        }
        if (vCtx.claims[attrData.claim2].dataType !== vCtx.attributes[attrData.id].dataType) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Claim '${attrData.claim}' of non-string type '${vCtx.claims[attrData.claim].dataType
            }' used in string concatention for AAD attribute '${attrData.id}'`,
          );
        }
        validateClaimIsSet(vCtx, node, attrData.claim2, `used in AAD ${nodeType} attribute mapping`);
      } else {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Unknown transformation function type '${attrData.transformation}' mapped to AAD attribute '${attrData.id}'`,
        );
      }
    }
  });

  if (node.nodeData.mapObjectId) {
    if (!node.nodeData.mappedClaim) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `ObjectId output mapping enabled for AAD ${nodeType} node, but no claim selected to map to it`,
      );
    } else {
      if (validateClaimExists(vCtx, node, node.nodeData.mappedClaim)) {
        if (vCtx.claims[node.nodeData.mappedClaim].dataType !== 'string') {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Claim ${node.nodeData.mappedClaim} mapped to object ID in AAD ${nodeType} node is of non-string type '${vCtx.claims[node.nodeData.mappedClaim].dataType
            }'`,
          );
        }
      }
    }
  }

  return uiCollectionOps;
};

const validateAADUpdateNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    validateNodeChildCount(vCtx, node, 1);
  }

  let uiCollectionOps = validateAADWriteMappings(vCtx, node, 'update');

  if (uiCollectionOps.has('remove') && uiCollectionOps.has('replace')) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'Replace and remove user identity collection operations cannot be used in the same AAD write operation',
    );
  }

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateAADCreateNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    validateNodeChildCount(vCtx, node, 1);
  }

  let uiCollectionOps = validateAADWriteMappings(vCtx, node, 'create');

  // Check for required mappings unique to AAD create.
  let hasDisplayName = false;
  let hasPassword = false;
  node.nodeData.attributes.forEach((attrData: any) => {
    if (attrData.id === 'displayName') {
      hasDisplayName = true;
      if (!attrData.claim) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          'Required mapping for displayName in AAD Create node has no claim selected',
        );
      }
    }
    if (attrData.id === 'password') {
      hasPassword = true;
      if (attrData.claimOrFunc !== "Random") {
        if (!attrData.claim) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            'Required mapping for password in AAD Create node has no claim selected',
          );
        }
      }
    }
  });

  if (!hasDisplayName) {
    addNodeErrorReport(node.nodeId, vCtx, 'Required mapping for displayName in AAD Create node is missing');
  }
  if (!hasPassword) {
    addNodeErrorReport(node.nodeId, vCtx, 'Required mapping for password in AAD Create node is missing');
  }

  if (uiCollectionOps.has('remove')) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'Remove user identity collection operations cannot be used in AAD create operation',
    );
  }

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateRestAPINode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    validateNodeChildCount(vCtx, node, 1);
  }

  if (templatizableFieldIsEmpty(node.nodeData.serviceUrl)) {
    addNodeErrorReport(node.nodeId, vCtx, 'No service URL provided for REST API node');
  } else if (node.nodeData.serviceUrl.templatized) {
    validateSettingDefinedInAllEnvs(
      vCtx,
      node.nodeData.serviceUrl.value,
      node
    );
  }

  if (node.nodeData.authenticationType === '') {
    addNodeErrorReport(node.nodeId, vCtx, 'No authentication method specified for REST API node');
  } else if (node.nodeData.authenticationType === 'aadBearer') {
    if (templatizableFieldIsEmpty(node.nodeData.clientIdContainer)) {
      addNodeErrorReport(node.nodeId, vCtx, 'REST APIs using Azure AD bearer tokens for authentication must specify a container name for the client ID to use for obtaining an AAD token')
    }
    if (templatizableFieldIsEmpty(node.nodeData.clientSecretContainer)) {
      addNodeErrorReport(node.nodeId, vCtx, 'REST APIs using Azure AD bearer tokens for authentication must specify a container name for the client secret to use for obtianing an AAD token')
    }
    if (templatizableFieldIsEmpty(node.nodeData.issuerTenant)) {
      addNodeErrorReport(node.nodeId, vCtx, 'REST APIs using Azure AD bearer tokens for authentication must specify the tenant containing the application to request the access token from')
    }
    if (templatizableFieldIsEmpty(node.nodeData.applicationIdUri)) {
      addNodeErrorReport(node.nodeId, vCtx, 'REST APIs using Azure AD bearer tokens for authentication must specify the application ID URL for the application to request the access token from')
    }
    if (checkAndSetRESTAADBearerTokenInfo(vCtx, {
      clientIdContainer: getTemplatizableFieldValue(vCtx, node.nodeData.clientIdContainer),
      clientSecretContainer: getTemplatizableFieldValue(vCtx, node.nodeData.clientSecretContainer),
      issuerTenant: getTemplatizableFieldValue(vCtx, node.nodeData.issuerTenant),
      applicationIdUri: getTemplatizableFieldValue(vCtx, node.nodeData.applicationIdUri)
    })) {
      addNodeErrorReport(node.nodeId, vCtx, `Multiple REST API calls using AAD bearer token authentication request tokens from the same application ID (in container ${getTemplatizableFieldValue(vCtx, node.nodeData.clientIdContainer)}), but specify different issuer tenants and/or application ID URIs`);
    }
  } else if ((node.nodeData.authenticationType === 'clientCert') && (templatizableFieldIsEmpty(node.nodeData.clientCertContainer))) {
    addNodeErrorReport(node.nodeId, vCtx, 'REST APIs using a client certificate for authentiction must specify the container name of the certificate')
  } else if (!['clientCert', 'aadBearer', 'none'].includes(node.nodeData.authenticationType)) {
    addNodeErrorReport(node.nodeId, vCtx, 'Unknown authentication method specified for REST API node');
  }

  node.nodeData.parameters.forEach((paramData: any) => {
    if (!paramData.parameterName) {
      addNodeErrorReport(node.nodeId, vCtx, 'Empty paramter name in REST API node');
    }
    if (!paramData.sourceType || paramData.sourceType === 'claim') {
      if (!paramData.claimName) {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `No claim mapped to parameter '${paramData.parameterName}' in REST API node`,
        );
      }
      if (validateClaimExists(vCtx, node, paramData.claimName)) {
        if (paramData.type === 'input') {
          validateClaimIsSet(
            vCtx,
            node,
            paramData.claimName,
            `mapped to REST API parameter '${paramData.parameterName}'`,
            paramData.defaultValue ? paramData.defaultValue : undefined,
          );
        }
        if (
          vCtx.claims[paramData.claimName].dataType === 'boolean' &&
          paramData.defaultValue &&
          paramData.defaultValue !== 'true' &&
          paramData.defaultValue !== 'false'
        ) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Invalid default value for boolean claim ${paramData.claimType} mapped in REST API node`,
          );
        }
      }
    } else if (paramData.sourceType === 'resolver') {
      if (paramData.type !== 'input') {
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Claim resolver '${paramData.claimName}' mapped to non-input parameter '${paramData.parameterName}' of REST API`,
        );
      }
      if (
        Object.keys(constants.sessionContexts.customKeyResolvers).some((k) => {
          return paramData.claimName.startsWith(
            constants.sessionContexts.customKeyResolvers[k as keyof typeof constants.sessionContexts.customKeyResolvers]
              .prefix,
          );
        })
      ) {
        if (paramData.claimName.split(':')[1]?.length <= 1) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `No key value provided for custom-key claim resolver mapped to REST API parameter '${paramData.parameterName}'`,
          );
        }
      }
      if (isRealResolver(paramData.claimName)) {
        let resolverInfo = getRealResolverInfo(paramData.claimName);
        if (!resolverInfo) {
          addNodeErrorReport(node.nodeId, vCtx, `Unknown session context item '${paramData.claimName}'`);
        } else if (!resolverInfo.validFor.includes(vCtx.flowcharts[node.flowId].issuedTokenType!)) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Session context item '${paramData.claimName}' not available for the token type issued by its containing parent flowchart`,
          );
        }
      }
    }
  });

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateConditionalNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 2);

  if (node.nodeData.groups.length == 0) {
    addNodeErrorReport(node.nodeId, vCtx, 'No condition groups in conditional node');
  }
  node.nodeData.groups.forEach((condGroup: any) => {
    if (condGroup.length == 0) {
      addNodeErrorReport(node.nodeId, vCtx, 'Empty condition group in conditional node');
    }
    condGroup.forEach((cond: any) => {
      if (validateClaimExists(vCtx, node, cond.claim)) {
        validateClaimIsSet(vCtx, node, cond.claim, 'used in conditional');
      }
      if (cond.compareTo === 'claim') {
        if (!cond.comValue) {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `No claim identified to compare against claim '${cond.claim}' in conditional`,
          );
        }
        if (validateClaimExists(vCtx, node, cond.comValue)) {
          validateClaimIsSet(vCtx, node, cond.comValue, 'used in conditional');
        }
      } else if (cond.compareTo === 'literal') {
        if (vCtx.claims[cond.claim].dataType === 'boolean') {
          if (cond.comValue !== 'true' && cond.comValue !== 'false') {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Boolean claim '${cond.claim}' compared against non-boolean literal value in condiitonal`,
            );
          }
        }
      } else if (cond.compareTo === 'resolver') {
        if (isRealResolver(cond.comValue) && vCtx.claims[cond.claim].dataType !== 'string') {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Non-string claim '${cond.claim}' compared against string-valued session context item in conditional`,
          );
        } else if (isSpecialResolver(cond.comValue)) {
          let specialType =
            constants.specialClaims[
              constants.sessionContexts.specials[cond.comValue as keyof typeof constants.sessionContexts.specials]
                .claimId as keyof typeof constants.specialClaims
            ].dataType;
          if (vCtx.claims[cond.claim].dataType !== specialType) {
            addNodeErrorReport(
              node.nodeId,
              vCtx,
              `Claim '${cond.claim}' of data type '${vCtx.claims[cond.claim].dataType
              }' compared against session context item of non-matching type ${specialType} in conditional`,
            );
          }
        }
      }
    });
  });
};

const validateMFANode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'MFA components are not valid within submit action flows');
  }
  if (node.nodeData.mode !== 'alwaysNew') {
    if (validateClaimExists(vCtx, node, node.nodeData.phoneNumberClaim)) {
      validateClaimIsSet(vCtx, node, node.nodeData.phoneNumberClaim, 'used for input phone number in MFA node');
    }
  }
  if (validateClaimExists(vCtx, node, node.nodeData.idClaim)) {
    validateClaimIsSet(vCtx, node, node.nodeData.idClaim, 'used for ID source in MFA node');
  }
  validateClaimExists(vCtx, node, node.nodeData.enteredPhoneNumberClaim);
  validateClaimExists(vCtx, node, node.nodeData.newNumberFlagClaim);

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateWebAuthnAuthenticationNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'WebAuthn Authentication components are not valid within submit action flows',
    );
  }
  validateClaimPresence(vCtx, node, node.nodeData.idClaim, 'ID Claim', '', false);
  validateClaimPresence(vCtx, node, node.nodeData.successClaim, 'Success Flag Claim', '', false);

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateWebAuthnRegistrationNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'WebAuthn Registration components are not valid within submit action flows');
  }
  validateClaimPresence(
    vCtx,
    node,
    node.nodeData.idClaim,
    'ID Claim',
    'used for object ID in WebAuthn registration',
    true,
  );
  validateClaimPresence(
    vCtx,
    node,
    node.nodeData.nameClaim,
    'Name Claim',
    'used for user-visible signin name in WebAuthn registration',
    true,
  );
  validateClaimPresence(
    vCtx,
    node,
    node.nodeData.displayNameClaim,
    'Display Name Claim',
    'used for user-visible display name in WebAuthn registration',
    true,
  );
  validateClaimPresence(vCtx, node, node.nodeData.successClaim, 'Success Flag Claim', '', false);

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateWebAuthnAvailabilityNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'WebAuthn Availability components are not valid within submit action flows');
  }
  validateClaimPresence(vCtx, node, node.nodeData.availabilityClaim, 'Availability Claim', '', false);
  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateWebAuthnCredentialExistsNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'WebAuthn Credentials Exist components are not valid within submit action flows',
    );
  }

  validateClaimPresence(vCtx, node, node.nodeData.existsClaim, 'Credential Existence Claim', '', false);
  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateConditionalAccessEvaluationNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'Conditional Access Evaluation components are not valid within submit action flows',
    );
  }

  validateClaimPresence(
    vCtx,
    node,
    node.nodeData.idClaim,
    'ID Claim',
    'used for object ID in conditional access evaluation',
    true,
  );
  validateClaimPresence(vCtx, node, node.nodeData.mfaRegisteredClaim, 'MFA Registered Claim', '', false);
  validateClaimPresence(vCtx, node, node.nodeData.changePasswordChallenge, 'Must Change Password', '', false);
  validateClaimPresence(vCtx, node, node.nodeData.mfaChallenge, 'Must Perform MFA', '', false);
  validateClaimPresence(vCtx, node, node.nodeData.blockChallenge, 'Must Block User', '', false);
  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateConditionalAccessRemediationNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'Conditional Access Remediation components are not valid within submit action flows',
    );
  }

  // Do a reverse traversal of the flowchart to make sure all backwards paths hit a CA evaluation node.
  // In theory a remediation provider could be called without an evaluation happening first, but it shouldn't and it will also
  // not work at all under the component model.
  let visited: Set<string> = new Set();
  let toVisit: string[] = [node.nodeId];
  let alwaysEvaluated = true;
  while (toVisit.length > 0) {
    let nextId = toVisit.pop()!;
    if (!visited.has(nextId)) {
      visited.add(nextId);
      if (vCtx.nodes[nextId].parents.length === 0) {
        // Dead-end, without hitting a conditionalAccessEvaluation node
        alwaysEvaluated = false;
      }
      vCtx.nodes[nextId].parents.forEach((parentId) => {
        if (
          vCtx.nodes[parentId].nodeData.componentType !== constants.editorComponentTypes.conditionalAccessEvaluation
        ) {
          if (!visited.has(parentId)) {
            toVisit.push(parentId);
          }
        }
      });
    }
  }
  if (!alwaysEvaluated) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'At least one potential path leading to this Conditional Access Remediation does not include a Conditional Access Evaluation component',
    );
  }

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
};

const validateSettingDefinedInAllEnvs = (vCtx: JE_JsonValidationContext, setting: string, node: JE_FCValidationNode, valueValidation?: Function, validationMsg?: string) => {
  let failEnvs = Object.keys(vCtx.environments).reduce((acc: { empty: string[], invalid: string[] }, envId: any) => {
    const env = vCtx.environments[envId];
    if (!Object.keys(env.settings).includes(setting)) {
      acc.empty.push(env.name);
    } else {
      if (!env.settings[setting]) {
        acc.empty.push(env.name);
      } else if (valueValidation && (!valueValidation(env.settings[setting]))) {
        acc.invalid.push(env.name);
      }
    }
    return acc;
  }, { empty: [], invalid: [] });
  if (failEnvs.empty.length > 0) {
    const envStr = failEnvs.empty.join(', ');
    addNodeErrorReport(node.nodeId, vCtx, `Setting '${vCtx.settings[setting].name}' used in component '${node.displayLabel}' not set in environment(s) ${envStr}`, false);
  }
  if (failEnvs.invalid.length > 0) {
    let envStr = failEnvs.invalid.join(', ');
    if (validationMsg) {
      envStr = envStr + `: ${validationMsg}`;
    }
    addNodeErrorReport(node.nodeId, vCtx, `Setting '${vCtx.settings[setting].name}' used in component '${node.displayLabel}' has an invalid value in environment(s) ${envStr}`);
  }
}

const validateTokenIssuerNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 0);
  if (!node.nodeData.subClaim) {
    addNodeErrorReport(node.nodeId, vCtx, `No subject naming ('sub') claim specified for JWT token issuer node`);
  } else {
    if ((node.nodeData.protocol === 'saml') && templatizableFieldIsEmpty(node.nodeData.issuerUri)) {
      addNodeErrorReport(node.nodeId, vCtx, 'No issuer URI specified for SAML token issuer');
    }
    if (node.nodeData.ssoScope && templatizableFieldIsEmpty(node.nodeData.ssoScope)) {
      addNodeErrorReport(node.nodeId, vCtx, 'No value specified for SSO scope');
    } else if (node.nodeData.ssoScope && node.nodeData.ssoScope.templatized) {
      validateSettingDefinedInAllEnvs(vCtx, node.nodeData.ssoScope.value, node, (value: string) => { return ['Tenant', 'Application', 'Policy', 'Suppressed'].includes(value) }, "Value must be one of 'Tenant', 'Application', 'Policy', or 'Suppressed'");
    }
    if (node.nodeData.ssoExpiryType && templatizableFieldIsEmpty(node.nodeData.ssoExpiryType)) {
      addNodeErrorReport(node.nodeId, vCtx, 'No value specific for SSO expiry type');
    } else if (node.nodeData.ssoExpiryType && node.nodeData.ssoExpiryType.templatized) {
      validateSettingDefinedInAllEnvs(vCtx, node.nodeData.ssoExpiryType.value, node, (value: string) => { return ['Rolling', 'Absolute'].includes(value) }, "Value must be one of 'Rolling' or 'Absolute'");
    }
    if (node.nodeData.ssoExpirySeconds) {
      if (templatizableFieldIsEmpty(node.nodeData.ssoExpirySeconds)) {
        addNodeErrorReport(node.nodeId, vCtx, 'No value specified for SSO expiry time');
      } else if (!node.nodeData.ssoExpirySeconds.templatized) {
        const iValue = parseInt(node.nodeData.ssoExpirySeconds.value);
        if (!((iValue >= 900) && (iValue <= 86400))) {
          addNodeErrorReport(node.nodeId, vCtx, 'Invalid duration for SSO expiry time; value must be between 900 and 86400 seconds');
        }
      } else if (node.nodeData.ssoExpirySeconds.templatized) {
        validateSettingDefinedInAllEnvs(vCtx, node.nodeData.ssoExpirySeconds.value, node, (value: string) => { const parsed = parseInt(value); return (parsed >= 900) && (parsed <= 86400); }, "Value must be a number of seconds between 900 and 86400");
      }
    }
    if (
      !node.nodeData.attributes.some((attrData: any) => {
        return attrData.attributeName === node.nodeData.subClaim;
      })
    ) {
      addNodeErrorReport(
        node.nodeId,
        vCtx,
        `Subject naming ('sub') claim '${node.nodeData.subClaim}' specified in token issuer not present in token attribute list`,
      );
    }
    node.nodeData.attributes.forEach((attrData: any) => {
      if (attrData.attributeName === node.nodeData.subClaim) {
        if (validateClaimExists(vCtx, node, node.nodeData.subClaim)) {
          validateClaimIsSet(
            vCtx,
            node,
            node.nodeData.subClaim,
            'used as sub claim in token issuer',
            attrData.defaultValue ? attrData.defaultValue : undefined,
          );
        }
      } else {
        if (validateClaimExists(vCtx, node, attrData.attributeName)) {
          if ((!attrData.defaultValue) && (!node.parentEmittedClaims.has(attrData.attributeName))) {
            addNodeErrorReport(node.nodeId, vCtx, `Claim '${attrData.attributeName}' in token issuer is not output by any component and has an empty default value`)
          }
        }
      }
    });
  }
};

const validateErrorPageNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 0);
  if (vCtx.flowcharts[node.flowId].flowType === 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'Error page components are not valid within submit action flows');
  }
  if (node.nodeData.entries) {
    node.nodeData.entries.forEach((ent: any) => {
      if (ent.sourceType === 'claim') {
        validateClaimPresence(vCtx, node, ent.source, 'Data source', 'displayed in error page', false);
      }
    });
  }
};

const validateValidationErrorNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 0);
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    addNodeErrorReport(node.nodeId, vCtx, 'Validation error components are only valid within submit action flows');
  }
}

const validateAuthorizeTokenNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 1);
  if (vCtx.flowcharts[node.flowId].flowType !== 'userInfoEndpoint') {
    addNodeErrorReport(node.nodeId, vCtx, 'AuthorizeToken components are only valid within user info endpoint flows');
  }
  if (vCtx.flowcharts[node.flowId].rootNodeId !== node.nodeId) {
    addNodeErrorReport(node.nodeId, vCtx, 'Authorize Token components must be the root of their endpoint flows');
  }
  if (node.nodeData.claims) {
    node.nodeData.claims.forEach((ent: any) => {
      validateClaimPresence(vCtx, node, ent.id, 'Claim ID', '', false);
    });
  }
};

const validateUserInfoIssuerNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  validateNodeChildCount(vCtx, node, 0);
  if (vCtx.flowcharts[node.flowId].flowType !== 'userInfoEndpoint') {
    addNodeErrorReport(node.nodeId, vCtx, 'User Info Issuer components are only valid within user info endpoint flows');
  }
  if (node.nodeData.claims) {
    node.nodeData.claims.forEach((ent: any) => {
      validateClaimPresence(vCtx, node, ent.id, 'Claim ID', 'for user info endpoint output', true, ent.defaultValue);
    });
  }
};

const validateAppInsightsNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'submitAction') {
    validateNodeChildCount(vCtx, node, 1);
  }

  if (node.nodeData.eventType === '') {
    addNodeErrorReport(node.nodeId, vCtx, `EventType cannot be empty`);
  }
  if (node.nodeData.claims.length === 0) {
    addNodeErrorReport(node.nodeId, vCtx, `Minimum one claim should be selected`);
  }
  node.nodeData.claims.forEach((attrData: any) => {
    validateClaimExists(vCtx, node, attrData.claimId);
  });
};

const validateSendEmailOTPNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (vCtx.flowcharts[node.flowId].flowType !== 'emailVerificationSendAction') {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      'SendEmailOTP components are only valid within a custom email verification flow',
    );
  }
  validateNodeChildCount(vCtx, node, 0);
  if (node.nodeData.language.sourceType === 'claim') {
    validateClaimPresence(
      vCtx,
      node,
      node.nodeData.language.source,
      'Claim ID',
      'as source for language for OTP email',
      true,
    );
  }
  if (!node.nodeData.emailTemplate) {
    addNodeErrorReport(node.nodeId, vCtx, 'No email template selected');
  }
  if (!node.nodeData.otpField) {
    addNodeErrorReport(node.nodeId, vCtx, 'No email template field specified for the OTP code');
  }
  node.nodeData.fields.forEach((fld: any) => {
    if (!fld.templateField) {
      addNodeErrorReport(node.nodeId, vCtx, 'No email template field name specified for field');
    }
    if (fld.sourceType === 'claim') {
      validateClaimPresence(
        vCtx,
        node,
        fld.source,
        'Claim ID',
        `as source for field '${fld.templateField}' in OTP email`,
        true,
      );
    }
  });
};

const validateSetClaimsNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (!['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[node.flowId].flowType)) {
    validateNodeChildCount(vCtx, node, 1);
  }

  if (Object.keys(node.nodeData.setClaims).length === 0) {
    addNodeErrorReport(node.nodeId, vCtx, 'No claims selected to be set in SetClaims component');
  }

  Object.keys(node.nodeData.setClaims).forEach((scId: any) => {
    const sc = node.nodeData.setClaims[scId];
    validateClaimPresence(vCtx, node, sc.destClaim, 'Claim to Set', 'as claim to be set', false);
    const destDataType = vCtx.claims[sc.destClaim].dataType;
    const srcValue = sc.sourceValue.value;
    if (sc.sourceType === 'claim') {
      validateClaimPresence(vCtx, node, srcValue, 'Source claim', `to copy into claim '${sc.destClaim}'`, true);
      const srcDataType = vCtx.claims[srcValue].dataType
      if (destDataType !== srcDataType) {
        addNodeErrorReport(node.nodeId, vCtx, `Claim '${srcValue}' used for source value has data type '${srcDataType}', which does not match the type of the destination claim '${sc.destClaim}' ('${destDataType}')`);
      }
    } else if (sc.sourceType === 'literal') {
      if (destDataType === 'boolean') {
        if (!['true', 'false'].includes(srcValue)) {
          addNodeErrorReport(node.nodeId, vCtx, `Invalid boolean literal value '${srcValue}' assigned to claim '${sc.destClaim}' (must be 'true' or 'false')`);
        }
      }
    } else if (sc.sourceType === 'resolver') {
      if (!Object.keys(jsonXMLConstants.sessionContexts.specials).includes(srcValue) && (destDataType !== 'string')) {
        addNodeErrorReport(node.nodeId, vCtx, `Session context item '${srcValue}' only be written to string-type claims but is written to claim '${sc.destClaim}' of type '${destDataType}'`);
      }
      if (Object.keys(jsonXMLConstants.sessionContexts.specials).includes(srcValue) && (getKeyedValue(srcValue, jsonXMLConstants.sessionContexts.specials).dataType !== destDataType)) {
        addNodeErrorReport(node.nodeId, vCtx, `Session context item '${srcValue}' assigned to claim '${sc.destClaim}' does not match data type (${destDataType}) of claim`)
      }
    } else if (sc.sourceType === 'appsetting') {
      if (destDataType === 'string') {
        validateSettingDefinedInAllEnvs(vCtx, srcValue, node);
      } else if (destDataType === 'boolean') {
        validateSettingDefinedInAllEnvs(vCtx, srcValue, node, (value: string) => { return ['true', 'false'].includes(value); }, "Value must be one of 'true' or 'false'")
      }
    } else if (sc.sourceType === 'transform') {
      validateTransformJson(vCtx, node, sc.sourceValue.value, sc.transformData);
    }
  });

  validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
}

const validateSessionExistsNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[node.flowId].flowType)) {
    addNodeErrorReport(node.nodeId, vCtx, 'Session Exists components are not available within subflows');
  }
  validateNodeChildCount(vCtx, node, 2);
  if (node.inSSOSessionScope) {
    addNodeErrorReport(
      node.nodeId,
      vCtx,
      `Session Exists component descends from another Session Exists component and session will ${node.ssoSessionExists ? 'always' : 'never'} exist`,
    );
  }
  // Do a pair of quick graph traversals from each side of this node.
  // First, the session-exists side, to collect a set of nodes descending from it.
  let sessionExistsNodes: Set<string> = new Set();
  let toVisit: string[] = [];
  node.children
    .filter((chId) => {
      return node.allChildRefs
        .filter((chRef) => {
          return chRef.nodeId === chId;
        })
        .every((chRef) => {
          return chRef.edgeHandle === 'down';
        });
    })
    .forEach((chId) => {
      toVisit.push(chId);
    });
  while (toVisit.length > 0) {
    let nextId = toVisit.pop()!;
    if (!sessionExistsNodes.has(nextId)) {
      sessionExistsNodes.add(nextId);
      vCtx.nodes[nextId].children.forEach((chId) => {
        if (!sessionExistsNodes.has(chId)) {
          toVisit.push(chId);
        }
      });
    }
  }
  // Second, the session-does-not-exist side. Propagate the list of claims that have been set in the session, intersecting it when a node has multiple parents.
  // Ensure that each location at where the session-does-not-exist side either ends at a non-error page or meets a node reachable from the session-exists side,
  let pClaimsMap: { [nodeId: string]: Set<string> } = {};
  let specialSigninPClaims: { [nodeId: string]: Set<string> } = {};
  let sessionDNENodes: Set<string> = new Set();
  toVisit = [];
  node.children
    .filter((chId) => {
      return node.allChildRefs
        .filter((chRef) => {
          return chRef.nodeId === chId;
        })
        .every((chRef) => {
          return chRef.edgeHandle !== 'down';
        });
    })
    .forEach((chId) => {
      toVisit.push(chId);
    });
  while (toVisit.length > 0) {
    let nextId = toVisit.pop()!;
    if (!sessionDNENodes.has(nextId)) {
      sessionDNENodes.add(nextId);
      if (![constants.editorComponentTypes.errorPage, constants.editorComponentTypes.validationError].includes(vCtx.nodes[nextId].nodeData.componentType)) {
        let allPClaims: Set<string> = new Set();
        vCtx.nodes[nextId].parents.forEach((parentId) => {
          if (pClaimsMap.hasOwnProperty(parentId)) {
            pClaimsMap[parentId].forEach((clmId) => {
              allPClaims.add(clmId);
            });
          }
          if (
            vCtx.nodes[nextId].nodeData.linkId &&
            vCtx.nodes[nextId].nodeData.linkType === 'localAccountSignIn' &&
            specialSigninPClaims.hasOwnProperty(parentId)
          ) {
            specialSigninPClaims[parentId].forEach((clmId) => {
              allPClaims.add(clmId);
            });
          }
        });
        pClaimsMap[nextId] = new Set();
        allPClaims.forEach((pClm) => {
          if (
            vCtx.nodes[nextId].parents.reduce((prev, cur) => {
              return (
                prev &&
                (pClaimsMap[cur].has(pClm) ||
                  (vCtx.nodes[nextId].nodeData.linkId &&
                    vCtx.nodes[nextId].nodeData.linkType === 'localAccountSignIn' &&
                    specialSigninPClaims.hasOwnProperty(cur) &&
                    specialSigninPClaims[cur].has(pClm)))
              );
            }, true)
          ) {
            pClaimsMap[nextId].add(pClm);
          }
        });
        // If this node uses the session, then we need to find all the available output claims and check for matches against the session's stored claims.
        if (
          vCtx.nodes[nextId].nodeData.sessionManagement &&
          vCtx.nodes[nextId].nodeData.sessionManagement.option === 'useGlobalSession'
        ) {
          let { outputClaims, pwOutputClaims } = getNodeOutputClaims(vCtx, nextId);
          let bubbledSubmitActionClaims: Set<string> = vCtx.nodes[nextId].nodeData.formSubmitAction
            ? vCtx.flowcharts.hasOwnProperty(vCtx.nodes[nextId].nodeData.formSubmitAction.id)
              ? vCtx.flowcharts[vCtx.nodes[nextId].nodeData.formSubmitAction.id].chartOutputClaims
              : new Set()
            : new Set();
          let bubbledEmailVerificationClaims: Set<string> = new Set();
          if (
            vCtx.nodes[nextId].nodeData.componentType === constants.editorComponentTypes.userInput &&
            vCtx.nodes[nextId].nodeData.validateEmail &&
            vCtx.nodes[nextId].nodeData.emailVerificationSendAction &&
            vCtx.nodes[nextId].nodeData.emailVerificationSendAction.id &&
            vCtx.flowcharts.hasOwnProperty(vCtx.nodes[nextId].nodeData.emailVerificationSendAction.id) &&
            vCtx.flowcharts[vCtx.nodes[nextId].nodeData.emailVerificationSendAction.id].chartOutputClaims
          ) {
            bubbledEmailVerificationClaims =
              vCtx.flowcharts[vCtx.nodes[nextId].nodeData.emailVerificationSendAction.id].chartOutputClaims;
          }
          vCtx.ssoSession.persistedClaims.forEach((pClm) => {
            if (
              !pClaimsMap[nextId].has(pClm) &&
              (outputClaims.has(pClm) ||
                bubbledSubmitActionClaims.has(pClm) ||
                bubbledEmailVerificationClaims.has(pClm))
            ) {
              if (vCtx.nodes[nextId].nodeData.componentType !== 'signIn') {
                // SignIn nodes don't propagate their own persisted claims, except to a localAccountSignIn node
                pClaimsMap[nextId].add(pClm);
              } else {
                if (!specialSigninPClaims.hasOwnProperty(nextId)) {
                  specialSigninPClaims[nextId] = new Set();
                }
                specialSigninPClaims[nextId].add(pClm);
              }
            }
          });
        }
        // If there is a path off the session-does-not-exist branch from this node, it needs to have populated the session.
        if (
          vCtx.nodes[nextId].children.some((chId) => {
            return sessionExistsNodes.has(chId);
          }) ||
          vCtx.nodes[nextId].children.length === 0
        ) {
          vCtx.ssoSession.persistedClaims.forEach((pClm) => {
            if (!pClaimsMap[nextId].has(pClm)) {
              addNodeErrorReport(
                node.nodeId,
                vCtx,
                `Claim '${pClm}' persisted in SSO session is not stored in the session cookie on the session-does-not-exist path on some route through node '${vCtx.nodes[nextId].displayLabel}'`,
                false,
              );
            }
          });
        }
        // Add any children of this node that have had all their parents searched to the queue for searching next.
        vCtx.nodes[nextId].children
          .filter((chId) => {
            return vCtx.nodes[chId].parents.every((parentId) => sessionDNENodes.has(parentId));
          })
          .forEach((chId) => {
            toVisit.push(chId);
          });
      }
    }
  }
};

const validateOIDCIdPNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[node.flowId].flowType)) {
    addNodeErrorReport(node.nodeId, vCtx, 'OIDC IdP Authentication components are not available within subflows');
  } else {
    validateNodeChildCount(vCtx, node, 1);

    if (!node.nodeData.idpDomain) {
      addNodeErrorReport(node.nodeId, vCtx, 'Missing IdP domain');
    }
    if (!node.nodeData.metadataUrl) {
      addNodeErrorReport(node.nodeId, vCtx, 'Missing metadata URL');
    }
    if (!node.nodeData.clientId) {
      addNodeErrorReport(node.nodeId, vCtx, 'Missing application client ID');
    }
    if (!node.nodeData.responseType) {
      addNodeErrorReport(node.nodeId, vCtx, 'No response type selected');
    }
    if ((node.nodeData.responseType === 'code') && (!node.nodeData.clientSecretContainerName)) {
      addNodeErrorReport(node.nodeId, vCtx, 'Response type of code selected but client secret container name is missing');
    }
    if (!node.nodeData.responseMode) {
      addNodeErrorReport(node.nodeId, vCtx, 'No response mode selected');
    }
    if (!node.nodeData.issuerIdPartnerClaimType) {
      addNodeErrorReport(node.nodeId, vCtx, 'Missing partner claim type for issuer ID');
    }

    let seenPCT: Set<string> = new Set();

    node.nodeData.inputClaims.forEach((iClm: any) => {
      if (seenPCT.has(iClm.partnerClaimType)) {
        addNodeErrorReport(node.nodeId, vCtx, 'Same parter claim type mapped to more than one input claim');
      }
      seenPCT.add(iClm.partnerClaimType);
      validateClaimPresence(
        vCtx,
        node,
        iClm.claimId,
        'Input Claim',
        'used as input claim',
        true
      );
    });

    let seenClaim: Set<string> = new Set();
    node.nodeData.outputClaims.forEach((oClm: any) => {
      if (seenClaim.has(oClm.claimId)) {
        addNodeErrorReport(node.nodeId, vCtx, 'Same output claim mapped to more than one partner claim type');
      }
      seenClaim.add(oClm.claimId)
    });

    validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
  }
}

const validateSAMLIdPNode = (vCtx: JE_JsonValidationContext, node: JE_FCValidationNode) => {
  if (['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[node.flowId].flowType)) {
    addNodeErrorReport(node.nodeId, vCtx, 'SAML IdP Authentication components are not available within subflows');
  } else {
    validateNodeChildCount(vCtx, node, 1);

    if (!node.nodeData.idpDomain) {
      addNodeErrorReport(node.nodeId, vCtx, 'Missing IdP domain');
    }
    if (!node.nodeData.metadataUrl) {
      addNodeErrorReport(node.nodeId, vCtx, 'Missing metadata URL');
    }
    if (node.nodeData.signRequests) {
      if (!node.nodeData.signatureAlgorithm) {
        addNodeErrorReport(node.nodeId, vCtx, 'Requests are configured to be signed but no signature algorithm is selected');
      }
      if (!node.nodeData.messageSigningKey) {
        addNodeErrorReport(node.nodeId, vCtx, 'Requests are configured to be signed but no message signing key container is specified');
      }
    }
    if (node.nodeData.assertionsEncrypted && (!node.nodeData.assertionDecryptionKey)) {
      addNodeErrorReport(node.nodeId, vCtx, 'Assertion encryption is enabled but no decryption key container is specified');
    }
    if (node.nodeData.requestUser && (!node.nodeData.requestUserClaim)) {
      addNodeErrorReport(node.nodeId, vCtx, 'No claim selected for source of issuer User ID to send with SAML request');
    }

    let seenClaim: Set<string> = new Set();
    node.nodeData.outputClaims.forEach((oClm: any) => {
      if (seenClaim.has(oClm.claimId)) {
        addNodeErrorReport(node.nodeId, vCtx, 'Same output claim mapped to more than one partner claim type');
      }
      seenClaim.add(oClm.claimId)
    });

    validateSessionManagementSettings(vCtx, node, node.nodeData.sessionManagement);
  }
}

const validateNode = (vCtx: JE_JsonValidationContext, nodeId: string) => {
  let node = vCtx.nodes[nodeId];
  switch (node.nodeData.componentType) {
    case constants.editorComponentTypes.signIn:
      validateSignInNode(vCtx, node);
      break;
    case constants.editorComponentTypes.userInput:
      validateUserInputPageNode(vCtx, node);
      break;
    case constants.editorComponentTypes.google:
      validateGoogleNode(vCtx, node);
      break;
    case constants.editorComponentTypes.usernamePasswordValidation:
      validateUsernamePasswordValidationNode(vCtx, node);
      break;
    case constants.editorComponentTypes.aadRead:
      validateAADReadNode(vCtx, node);
      break;
    case constants.editorComponentTypes.aadUpdate:
      validateAADUpdateNode(vCtx, node);
      break;
    case constants.editorComponentTypes.aadCreate:
      validateAADCreateNode(vCtx, node);
      break;
    case constants.editorComponentTypes.rest:
      validateRestAPINode(vCtx, node);
      break;
    case constants.editorComponentTypes.conditional:
      validateConditionalNode(vCtx, node);
      break;
    case constants.editorComponentTypes.mfa:
      validateMFANode(vCtx, node);
      break;
    case constants.editorComponentTypes.webAuthnAuthentication:
      validateWebAuthnAuthenticationNode(vCtx, node);
      break;
    case constants.editorComponentTypes.webAuthnRegistration:
      validateWebAuthnRegistrationNode(vCtx, node);
      break;
    case constants.editorComponentTypes.webAuthnCredentialsExist:
      validateWebAuthnCredentialExistsNode(vCtx, node);
      break;
    case constants.editorComponentTypes.webAuthnAvailability:
      validateWebAuthnAvailabilityNode(vCtx, node);
      break;
    case constants.editorComponentTypes.tokenIssuer:
      validateTokenIssuerNode(vCtx, node);
      break;
    case constants.editorComponentTypes.conditionalAccessEvaluation:
      validateConditionalAccessEvaluationNode(vCtx, node);
      break;
    case constants.editorComponentTypes.conditionalAccessRemediation:
      validateConditionalAccessRemediationNode(vCtx, node);
      break;
    case constants.editorComponentTypes.errorPage:
      validateErrorPageNode(vCtx, node);
      break;
    case constants.editorComponentTypes.authorizeToken:
      validateAuthorizeTokenNode(vCtx, node);
      break;
    case constants.editorComponentTypes.userInfoIssuer:
      validateUserInfoIssuerNode(vCtx, node);
      break;
    case constants.editorComponentTypes.appInsights:
      validateAppInsightsNode(vCtx, node);
      break;
    case constants.editorComponentTypes.sendEmailOTP:
      validateSendEmailOTPNode(vCtx, node);
      break;
    case constants.editorComponentTypes.sessionExists:
      validateSessionExistsNode(vCtx, node);
      break;
    case constants.editorComponentTypes.oidcIdP:
      validateOIDCIdPNode(vCtx, node);
      break;
    case constants.editorComponentTypes.samlIdP:
      validateSAMLIdPNode(vCtx, node);
      break;
    case constants.editorComponentTypes.validationError:
      validateValidationErrorNode(vCtx, node);
      break;
    case constants.editorComponentTypes.setClaims:
      validateSetClaimsNode(vCtx, node);
      break;
    default:
      const addErrorReport = (message: string) => addNodeErrorReport(nodeId, vCtx, message);
      const validateClaimIsSet = (
        claimId: string,
        claimFieldName: string,
        mustBeSet: boolean,
        claimDescription?: string,
        defaultValue?: string,
        localNodeClaims?: Set<string>,
        localSAClaims?: Set<string>
      ) => validateClaimPresence(
        vCtx,
        node,
        claimId,
        claimFieldName,
        claimDescription || "",
        mustBeSet,
        defaultValue,
        localNodeClaims,
        localSAClaims,
      );
      validateEditorNodeSettings(node, vCtx, addErrorReport, validateClaimIsSet);
      break;
  }
  if (vCtx.flowcharts[node.flowId].flowType === 'emailVerificationSendAction') {
    if (node.nodeData.componentType !== 'sendEmailOTP' && node.children.length === 0) {
      addNodeErrorReport(
        nodeId,
        vCtx,
        `Only Send Email OTP components can be the final component in a custom email verification flow`,
      );
    }
  }
};

const parseSSOSession = (vCtx: JE_JsonValidationContext, jsonSessions: { [index: string]: any }) => {
  if (jsonSessions) {
    jsonSessions.persistedClaims.forEach((claimInfo: any) => {
      vCtx.ssoSession.persistedClaims.push(claimInfo.claimId);
    });
    jsonSessions.outputClaims.forEach((claimInfo: any) => {
      vCtx.ssoSession.outputClaims.push({ claimId: claimInfo.claimId, defaultValue: claimInfo.defaultValue });
    })
    Object.keys(jsonSessions.sessionFlags).forEach((sFlag: string) => {
      vCtx.ssoSession.sessionFlags.push(sFlag);
    });
  }
};

interface RESTAADTokenInfo {
  clientIdContainer: string;
  clientSecretContainer: string;
  applicationIdUri: string;
  issuerTenant: string;
}

// Checks the global running state of the validation context to see if a token is already being requested for the same client ID
// elsewhere in the flowchart so far. If not, it stores a record of the token in that state. If so, it checks for conflicting applicationId/issuerTenant.
// Regardless of whether the token was newly added to the state, returns false where there is no conflict and return true where there is.
const checkAndSetRESTAADBearerTokenInfo = (vCtx: JE_JsonValidationContext, tokenInfo: RESTAADTokenInfo) => {
  let key = tokenInfo.clientIdContainer;
  if (!vCtx.globalTracking.hasOwnProperty("restAadAccessTokens")) {
    vCtx.globalTracking.restAadAccessTokens = {};
  }
  if (!vCtx.globalTracking.restAadAccessTokens.hasOwnProperty(key)) {
    vCtx.globalTracking.restAadAccessTokens[key] = tokenInfo;
  } else if ((vCtx.globalTracking.restAadAccessTokens[key].applicationIdUri !== tokenInfo.applicationIdUri) || (vCtx.globalTracking.restAadAccessTokens[key].issuerTenant !== tokenInfo.issuerTenant)) {
    return true;
  }
  return false;
}

export const validateJson = (jsonBase: { [index: string]: any }) => {
  let { flowchartIndices, nodes } = buildGraph(jsonBase.flowcharts, jsonBase.nodeData);
  let errorReports: JE_JsonValidationErrorReportStore = { nodeErrors: {}, claimsErrors: {} };
  let vCtx: JE_JsonValidationContext = {
    nodes: nodes,
    flowcharts: flowchartIndices,
    claims: {},
    attributes: {},
    ssoSession: { persistedClaims: [], outputClaims: [], sessionFlags: [] },
    settings: jsonBase.templatization?.templateFields,
    environments: jsonBase.templatization?.environments,
    globalTracking: {},
    errors: errorReports,
  };
  parseClaims(vCtx, jsonBase.claims);
  parseAttributes(vCtx, jsonBase.aadAttributes);

  // Wire up nodes to their submit action flowcharts.
  Object.keys(nodes).forEach((nodeId: string) => {
    let node = nodes[nodeId];
    if (node.nodeData.formSubmitAction && node.nodeData.formSubmitAction.id) {
      if (!jsonBase.flowcharts.hasOwnProperty(node.nodeData.formSubmitAction.id)) {
        // Check flowcharts instead of indices so empty charts are picked up
        addNodeErrorReport(
          node.nodeId,
          vCtx,
          `Node submit action flow with chart ID '${node.nodeData.formSubmitAction.id}' does not exist in flowcharts`,
        );
      } else if (flowchartIndices.hasOwnProperty(node.nodeData.formSubmitAction.id)) {
        if (flowchartIndices[node.nodeData.formSubmitAction.id].flowType !== 'submitAction') {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Node submit action flow with chart ID '${node.nodeData.formSubmitAction.id}' is a User Journey, not a submit action`,
          );
        } else {
          flowchartIndices[node.nodeData.formSubmitAction.id].subflowParentNode = node.nodeId;
        }
      }
    }
    if (node.nodeData.emailVerificationSendAction && node.nodeData.emailVerificationSendAction.id) {
      if (flowchartIndices.hasOwnProperty(node.nodeData.emailVerificationSendAction.id)) {
        if (flowchartIndices[node.nodeData.emailVerificationSendAction.id].flowType !== 'emailVerificationSendAction') {
          addNodeErrorReport(
            node.nodeId,
            vCtx,
            `Node custom email verification flow with chart ID '${node.nodeData.emailVerificationSendAction.id}' is not an email verification send action flow`,
          );
        } else {
          flowchartIndices[node.nodeData.emailVerificationSendAction.id].subflowParentNode = node.nodeId;
        }
      }
    }
  });
  // Do a graph traversal on each flowchart to check for cycles and disconnected segments.
  Object.keys(vCtx.flowcharts).forEach((flowId) => {
    let flowchart = vCtx.flowcharts[flowId];
    let rootConnectedCollection: { [nodeId: string]: Set<string> } = {};
    let potentialRoots: string[] = [];
    flowchart.nodes.forEach((nodeId: string) => {
      if (vCtx.nodes[nodeId].parents.length == 0) {
        potentialRoots.push(nodeId);
      }
    });
    potentialRoots.forEach((rootId: string) => {
      let tVisited: Set<string> = new Set();
      tVisited.add(rootId);
      let tQueue: string[] = [rootId];
      rootConnectedCollection[rootId] = new Set();
      while (tQueue.length > 0) {
        let nextId = tQueue.shift()!;
        rootConnectedCollection[rootId].add(nextId);
        vCtx.nodes[nextId].children.forEach((chId) => {
          if (!tVisited.has(chId)) {
            tQueue.push(chId);
            tVisited.add(chId);
          }
        });
      }
    });
    let bestRoot: string = '';
    let bestSize = 0;
    Object.keys(rootConnectedCollection).forEach((rootId) => {
      if (rootConnectedCollection[rootId].size > bestSize) {
        bestRoot = rootId;
        bestSize = rootConnectedCollection[rootId].size;
      }
    });
    potentialRoots.forEach((rootId) => {
      if (rootId !== bestRoot) {
        addNodeErrorReport(rootId, vCtx, 'Flow cannot have multiple root nodes');
      }
    });
    Object.keys(flowchart.nodes).forEach((nodeId) => {
      if (!rootConnectedCollection[bestRoot].has(nodeId)) {
        addNodeErrorReport(nodeId, vCtx, 'Node not reachable in flow structure');
        vCtx.nodes[nodeId].children.forEach((chId) => {
          vCtx.nodes[chId].parents.splice(vCtx.nodes[chId].parents.indexOf(nodeId));
        });
        vCtx.nodes[nodeId].children = [];
      }
    });
    flowchart.connectedNodes = rootConnectedCollection[bestRoot];
    flowchart.rootNodeId = bestRoot;
    if (
      flowchart.flowType === 'userInfoEndpoint' &&
      vCtx.nodes[flowchart.rootNodeId].nodeData.componentType !== 'authorizeToken'
    ) {
      addNodeErrorReport(
        flowchart.rootNodeId,
        vCtx,
        'Root node of user info endpoint flow must be an AuthorizeToken component',
      );
    }

    let visitParentMap: { [nodeId: string]: Set<string> } = {};
    let finished: Set<string> = new Set();
    finished.add(bestRoot);
    let tQueue: string[] = [bestRoot];
    while (tQueue.length > 0) {
      let nextId = tQueue.shift()!;
      flowchart.topologicalOrder.push(nextId);

      vCtx.nodes[nextId].children.forEach((chId) => {
        if (!visitParentMap.hasOwnProperty(chId)) {
          visitParentMap[chId] = new Set();
        }
        if (visitParentMap[chId].has(nextId)) {
          addNodeErrorReport(chId, vCtx, 'Cycle in flow structure');
        }
        visitParentMap[chId].add(nextId);
        if (!finished.has(chId)) {
          if (
            vCtx.nodes[chId].parents.every((pId) => {
              return visitParentMap[chId].has(pId);
            })
          ) {
            tQueue.push(chId);
            finished.add(chId);
          }
        }
      });
    }
  });
  // Iterate over all token issuer nodes and mark down the token type of their flows.
  Object.keys(vCtx.nodes).forEach((nodeId) => {
    let node = vCtx.nodes[nodeId];
    if (node.nodeData.componentType === 'tokenIssuer') {
      vCtx.flowcharts[node.flowId].issuedTokenType = node.nodeData.protocol;
    }
  });
  // Iterate over all flowcharts and mark subflows with the token type of their parents.
  Object.keys(vCtx.flowcharts).forEach((flowId) => {
    let flowchart = vCtx.flowcharts[flowId];
    if (flowchart.subflowParentNode) {
      flowchart.issuedTokenType = vCtx.flowcharts[vCtx.nodes[flowchart.subflowParentNode!].flowId].issuedTokenType;
    }
  });

  parseSSOSession(vCtx, jsonBase.ssoSession);

  // Iterate over nodes and track what claims are available for use where.
  Object.keys(vCtx.flowcharts).forEach((flowId) => {
    let flowchart = vCtx.flowcharts[flowId];
    if (!['submitAction', 'emailVerificationSendAction'].includes(flowchart.flowType)) {
      processFlowchartClaims(vCtx, flowId, new Set(), false, false, false);
    }
  });
  // Iterate over nodes and validate them individually. But go by flowchart so submit action flows that got disconnected can be excluded.
  Object.keys(vCtx.flowcharts).forEach((flowId) => {
    // Skip subflows that don't have a parent node - they're artifacts of node deletion
    if (
      !['submitAction', 'emailVerificationSendAction'].includes(vCtx.flowcharts[flowId].flowType) ||
      vCtx.flowcharts[flowId].subflowParentNode
    ) {
      vCtx.flowcharts[flowId].nodes.forEach((nodeId) => {
        validateNode(vCtx, nodeId);
      });
    }
  });
  let allErrors: JE_JsonValidationErrorReport[] = [];
  Object.keys(vCtx.errors.nodeErrors).forEach((nodeId) => {
    allErrors.push(vCtx.errors.nodeErrors[nodeId]);
  });
  Object.keys(vCtx.errors.claimsErrors).forEach((claimId) => {
    allErrors.push(vCtx.errors.claimsErrors[claimId]);
  });
  return allErrors;
};
